import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Chip, LinearProgress, Stack, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import useApiControl from './common/useApiControl';
import { SSEProvider, useSSEData } from './common/useSseControl';

const statusMap = {
  waiting: '開始待ち',
  fast: 'ベット受付中',
  active: 'ベット受付中',
  ended: '受付終了',
};

const parseAnswers = (rawAnswers) => {
  if (Array.isArray(rawAnswers)) {
    return rawAnswers;
  }

  if (typeof rawAnswers !== 'string') {
    return [];
  }

  const trimmed = rawAnswers.trim();
  if (!trimmed) {
    return [];
  }

  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Failed to parse round answers.', error);
    return [];
  }
};

const formatRemaining = (seconds) => {
  const safeSeconds = Math.max(0, Math.floor(seconds));
  const minutes = String(Math.floor(safeSeconds / 60)).padStart(2, '0');
  const secs = String(safeSeconds % 60).padStart(2, '0');
  return `${minutes}:${secs}`;
};

const buildUpdateMessage = (message) => {
  if (!message) {
    return '';
  }

  if (message.type === 'message' && message.action === 'notify') {
    return typeof message.payload === 'string' ? message.payload : `${message.target} 通知を受信`;
  }

  if (message.type === 'direct' && message.action === 'update' && message.target === 'roundSummary') {
    const totalBet = message.payload?.totalBet;
    if (typeof totalBet === 'number') {
      return `集計更新 合計 ${totalBet} pt`;
    }
    return '集計を更新しました';
  }

  if (message.type === 'direct' && message.action === 'update' && message.target === 'roundRate') {
    return 'オッズを更新しました';
  }

  if (message.type === 'system' && message.action === 'resync' && message.target === 'round') {
    return 'ラウンド情報を再同期しました';
  }

  if (message.type === 'system' && message.action === 'sync' && message.target === 'endTime') {
    return '終了時刻を同期しました';
  }

  return `${message.type}/${message.action}/${message.target}`;
};

const StreamSidebarContent = ({ event, round, setRound }) => {
  const { sseData, sseError } = useSSEData();
  const answers = useMemo(() => parseAnswers(round?.answers), [round?.answers]);
  const [rates, setRates] = useState([]);
  const [actionLogs, setActionLogs] = useState([]);
  const [remaining, setRemaining] = useState('00:00');
  const [status, setStatus] = useState('waiting');
  const [progress, setProgress] = useState(0);

  const roundParams = useMemo(
    () => ({
      event_id: event?.id,
      number: event?.round ?? round?.number,
    }),
    [event?.id, event?.round, round?.number]
  );

  const { executeRequest: executeRoundRequest } = useApiControl({
    action: '/get_round',
    params: roundParams,
    method: 'POST',
  });

  useEffect(() => {
    if (
      sseData?.type === 'system' &&
      (
        (sseData.action === 'resync' && sseData.target === 'round') ||
        (sseData.action === 'sync' && sseData.target === 'endTime')
      )
    ) {
      executeRoundRequest()
        .then((nextRound) => {
          if (nextRound) {
            setRound(nextRound);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [executeRoundRequest, setRound, sseData]);

  useEffect(() => {
    if (!event?.id || !round?.number) {
      return;
    }

    let cancelled = false;

    const syncLatestRound = () =>
      executeRoundRequest({
        event_id: event.id,
        number: event.round ?? round.number,
      })
        .then((nextRound) => {
          if (!cancelled && nextRound) {
            setRound(nextRound);
          }
        })
        .catch((error) => {
          console.error(error);
        });

    syncLatestRound();

    if (round?.starttime && round?.endtime) {
      return () => {
        cancelled = true;
      };
    }

    const intervalId = setInterval(syncLatestRound, 1500);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [event?.id, event?.round, executeRoundRequest, round?.endtime, round?.number, round?.starttime, setRound]);

  useEffect(() => {
    if (
      sseData?.type === 'direct' &&
      sseData?.action === 'update' &&
      sseData?.target === 'roundRate' &&
      Array.isArray(sseData?.payload)
    ) {
      setRates(sseData.payload.map((value) => Number(value)));
    }

    if (sseData) {
      const message = buildUpdateMessage(sseData);
      if (message) {
        setActionLogs((prev) => [message, ...prev].slice(0, 5));
      }
    }
  }, [sseData]);

  useEffect(() => {
    const updateTimer = () => {
      const now = new Date();
      const start = round?.starttime ? new Date(round.starttime) : null;
      const fast = round?.fasttime ? new Date(round.fasttime) : null;
      const end = round?.endtime ? new Date(round.endtime) : null;

      if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        setStatus('waiting');
        setRemaining('00:00');
        setProgress(0);
        return;
      }

      if (now < start) {
        const secondsUntilStart = (start.getTime() - now.getTime()) / 1000;
        setStatus('waiting');
        setRemaining(formatRemaining(secondsUntilStart));
        setProgress(0);
        return;
      }

      if (event?.bet_close_mode === 'manual') {
        if (round?.bet_closed_at) {
          setStatus('ended');
          setRemaining('00:00');
          setProgress(100);
        } else {
          setStatus('active');
          setRemaining(formatRemaining(Math.max((now.getTime() - start.getTime()) / 1000, 0)));
          setProgress(0);
        }
        return;
      }

      if (fast && !Number.isNaN(fast.getTime()) && now < fast) {
        setStatus('fast');
      } else if (now < end) {
        setStatus('active');
      } else {
        setStatus('ended');
      }

      if (now < end) {
        const secondsUntilEnd = (end.getTime() - now.getTime()) / 1000;
        const totalSeconds = Math.max(1, (end.getTime() - start.getTime()) / 1000);
        const elapsedSeconds = totalSeconds - Math.max(0, secondsUntilEnd);

        setRemaining(formatRemaining(secondsUntilEnd));
        setProgress(Math.min(100, Math.max(0, (elapsedSeconds / totalSeconds) * 100)));
        return;
      }

      setRemaining('00:00');
      setProgress(100);
    };

    updateTimer();
    const timerId = setInterval(updateTimer, 1000);
    return () => clearInterval(timerId);
  }, [event?.bet_close_mode, round?.bet_closed_at, round?.endtime, round?.fasttime, round?.starttime]);

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100%',
        boxSizing: 'border-box',
        p: 2,
        bgcolor: '#0f172a',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        overflow: 'hidden',
        borderLeft: '1px solid #1e293b',
      }}
    >
      <Box>
        <Typography variant="h5" fontWeight={800}>
          Betty
        </Typography>
        <Typography variant="h6" fontWeight={700}>
          {event?.name || 'ルーム情報'}
        </Typography>
        <Chip
          label={statusMap[status] || statusMap.waiting}
          color={status === 'ended' ? 'default' : 'warning'}
          sx={{ mt: 1, fontSize: '1rem', fontWeight: 700 }}
        />
      </Box>

      {sseError ? (
        <Alert severity="error" sx={{ bgcolor: '#fff' }}>
          SSE の接続に失敗しました
        </Alert>
      ) : null}

      <Box sx={{ borderTop: '1px solid #334155', pt: 2 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          第{round?.number || event?.round || 1}ラウンド
        </Typography>
        <Typography variant="h3" fontWeight={900}>
          {event?.bet_close_mode === 'manual'
            ? (round?.bet_closed_at ? 'ベット終了' : `経過 ${remaining}`)
            : `残り ${remaining}`}
        </Typography>
        {event?.bet_close_mode !== 'manual' && (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 10, borderRadius: 5, mt: 1 }}
          />
        )}
      </Box>

      <Box sx={{ borderTop: '1px solid #334155', pt: 2 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          現在の選択肢
        </Typography>
        <Stack spacing={1} sx={{ mt: 1 }}>
          {answers.length === 0 ? (
            <Typography variant="body2" color="rgba(255,255,255,0.7)">
              選択肢はまだ設定されていません
            </Typography>
          ) : (
            answers.slice(0, 4).map((answer, index) => {
              const rate = Number(rates[index] || 0);
              const percent = rate > 0 ? Math.min(100, Math.round((1 / rate) * 100)) : 0;

              return (
                <Box key={`${answer}-${index}`} sx={{ bgcolor: '#1e293b', borderRadius: 1, p: 1.25 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography fontWeight={700}>{answer}</Typography>
                    <Typography whiteSpace="nowrap">
                      {percent}% / {rate > 0 ? `${rate.toFixed(2)}倍` : '-'}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={percent}
                    sx={{ mt: 0.75, height: 8, borderRadius: 4 }}
                  />
                </Box>
              );
            })
          )}
        </Stack>
      </Box>

      <Box sx={{ borderTop: '1px solid #334155', pt: 2, flexGrow: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          最新の更新
        </Typography>
        <Stack spacing={0.75} sx={{ mt: 0.75 }}>
          {actionLogs.length === 0 ? (
            <Typography variant="body2" color="rgba(255,255,255,0.7)">
              更新待機中...
            </Typography>
          ) : (
            actionLogs.map((log, index) => (
              <Typography key={`${log}-${index}`} variant="body2">
                ・{log}
              </Typography>
            ))
          )}
        </Stack>
      </Box>

      <Box sx={{ borderTop: '1px solid #334155', pt: 2 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          配信 URL
        </Typography>
        <Typography variant="body2" sx={{ wordBreak: 'break-all', color: 'rgba(255,255,255,0.8)' }}>
          /stream/{event?.room_id || ''}
        </Typography>
      </Box>
    </Box>
  );
};

export default function StreamView() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [round, setRound] = useState(null);

  const params = useMemo(
    () => ({
      room_id: eventId,
    }),
    [eventId]
  );

  const { data, error, loading, executeRequest } = useApiControl({
    action: '/get_event_context',
    params,
    method: 'POST',
  });

  useEffect(() => {
    if (!eventId) {
      return;
    }

    executeRequest().catch((requestError) => {
      console.error(requestError);
    });
  }, [eventId, executeRequest]);

  useEffect(() => {
    if (!data) {
      return;
    }

    setEvent(data.event ?? null);
    setRound(data.round ?? null);
  }, [data]);

  const sseUrl = event?.id ? `/api/sse_updates?index=${event.id}` : '';

  if (loading && !event && !round) {
    return (
      <Box sx={{ p: 3, minHeight: '100dvh', boxSizing: 'border-box' }}>
        <Typography>読み込み中...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, minHeight: '100dvh', boxSizing: 'border-box' }}>
        <Alert severity="error">イベント情報の取得に失敗しました</Alert>
      </Box>
    );
  }

  if (!event || !round || !sseUrl) {
    return (
      <Box sx={{ p: 3, minHeight: '100dvh', boxSizing: 'border-box' }}>
        <Typography>表示できる配信データがありません</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minHeight: '100dvh',
        display: 'flex',
        bgcolor: '#020617',
        color: '#fff',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'stretch',
          p: 2,
          boxSizing: 'border-box',
          background:
            'radial-gradient(circle at top left, rgba(59,130,246,0.18), transparent 28%), linear-gradient(180deg, #020617 0%, #000000 100%)',
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            borderRadius: 3,
            border: '1px dashed rgba(148,163,184,0.28)',
            bgcolor: 'rgba(15,23,42,0.28)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            p: 3,
            boxSizing: 'border-box',
          }}
        >
          <Box>
            <Typography variant="overline" sx={{ color: '#38bdf8', letterSpacing: '0.18em' }}>
              MAIN CONTENT AREA
            </Typography>
            <Typography variant="h4" fontWeight={800} sx={{ mt: 1 }}>
              ゲーム画面配置エリア
            </Typography>
            <Typography variant="body1" sx={{ mt: 1.5, color: 'rgba(226,232,240,0.82)', maxWidth: 520 }}>
              OBS ではこの左側にゲーム画面やカメラを重ね、右側の StreamView を情報パネルとして使う想定です。
            </Typography>
          </Box>

          <Box
            sx={{
              alignSelf: 'flex-start',
              px: 1.5,
              py: 0.75,
              borderRadius: 999,
              bgcolor: 'rgba(15,23,42,0.72)',
              border: '1px solid rgba(56,189,248,0.35)',
            }}
          >
            <Typography variant="body2" sx={{ color: '#bae6fd' }}>
              左: メイン映像 / 右: Betty 情報
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          width: { xs: '34vw', sm: 360, md: 390, lg: 420 },
          minWidth: { xs: 320, sm: 360, md: 390, lg: 420 },
          maxWidth: 420,
          minHeight: '100dvh',
          boxShadow: '-16px 0 32px rgba(0, 0, 0, 0.35)',
        }}
      >
        <SSEProvider url={sseUrl}>
          <StreamSidebarContent event={event} round={round} setRound={setRound} />
        </SSEProvider>
      </Box>
    </Box>
  );
}
